import { Injectable, signal, effect, computed, Signal } from '@angular/core';
import { MediaReferenceMetadataCreateModel } from './models/media-reference-metadata-create.model';
import { ArticleReferenceModel } from '@app/models/article-reference.model';
import { MediaReferenceMetadataModel } from '@app/models/media-reference-metadata.model';
import { IncidentModel } from '@app/models/incident.model';
import { isEqual } from 'lodash';

// This Store should only be provided as scoped in the IncidentsOverviewPage
// All child components of the page will inherit the same instance
// Modals however do not inherit the injection scope of the page/child component
// Opening modals requires you to provide the angular injector of the current component to the dialog.open({ injector: this.injector })
@Injectable()
export class IncidentDetailsPageStore {
  // We are using "incidentOriginal" when the page is not in edit mode.
  // "incidentEdit" is used as a copy when entering edit mode so it can be manipulated and not touch the original state.
  private incidentOriginal = signal<IncidentModel | null>(null);
  private incidentEdit = signal<IncidentModel | null>(null);
  private mergeViewOriginalIncident = signal<IncidentModel | null>(null);
  private mergeViewDuplicateIncidents = signal<IncidentModel[] | null>(null);

  constructor() {
    effect(() => {
      if (!this.isEditMode()) {
        // Exit edit mode. Reset state.
        this.resetData();
      } else {
        // Enter edit mode. Load the snapshot data.
        this.loadEditData();
      }
    });
  }

  // -- Setters

  public setIncident(incident: IncidentModel): void {
    this.incidentOriginal.set(incident);
    this.setIncidentEditFromOriginal(); // Making a copy of the original incident
  }

  public setIncidentEditFromOriginal(): void {
    this.incidentEdit.set(JSON.parse(JSON.stringify(this.incidentOriginal())));
  }

  public updateIncidentEditDetails(values: {
    title: string;
    summary: string;
    motivation: IncidentModel['motivation'];
    isReviewed: boolean;
    startedAt: string | null;
    endedAt: string | null;
  }): void {
    if (!this.isEditMode()) {
      return;
    }

    this.incidentEdit.update((incident) => {
      if (!incident) {
        return incident;
      }

      return {
        ...incident,
        ...values,
      };
    });
  }

  // We update fields of the incident model from here in order to trigger all effects/computeds that use the incidentOriginal/incidentEdit signals
  public updateIncidentRootField<K extends Extract<keyof IncidentModel, string>>(
    field: K,
    value: IncidentModel[K],
  ): void {
    const targetIncidentSignal = this.isEditMode() ? this.incidentEdit : this.incidentOriginal;

    targetIncidentSignal.update((incident) => {
      if (!incident) {
        return incident;
      }

      return {
        ...incident,
        [field]: value,
      };
    });

    if (field === 'technicalSteps') {
      this.isRimsEdited = true;
    }
  }

  // -- Properties and computeds

  // Marks if the incident is in edit mode
  public isEditMode = signal(false);

  // Marks if the incident's status is pending
  public isPending = signal(false);

  // Returns the snapshot or the original state based on the edit mode.
  public readonly incident: Signal<IncidentModel | null> = computed(() => {
    return this.isEditMode() ? this.incidentEdit() : this.incidentOriginal();
  });

  // Always returns the original state, regardless of the edit mode.
  public readonly uneditedIncident: Signal<IncidentModel | null> = computed(() => {
    return this.incidentOriginal();
  });

  public readonly mergeViewOriginal = this.mergeViewOriginalIncident.asReadonly();
  public readonly mergeViewDuplicates = this.mergeViewDuplicateIncidents.asReadonly();

  public setMergeViewCacheData(originalIncident: IncidentModel, duplicateIncidents: IncidentModel[]): void {
    this.mergeViewOriginalIncident.set(originalIncident);
    this.mergeViewDuplicateIncidents.set(duplicateIncidents);
  }

  public shouldFetchMergeViewData(): boolean {
    return this.mergeViewOriginalIncident() == null || this.mergeViewDuplicateIncidents() == null;
  }

  public clearMergeViewCache(): void {
    this.resetMergeViewCache();
  }

  // Compares the orignal entity and the snapshot entity of the incident.
  public isIncidentEntityModified(): boolean {
    return isEqual(this.incidentOriginal()!.entity, this.incidentEdit()!.entity);
  }

  // Compares the orignal entity and the snapshot entity of the incident.
  public isIncidentActorModified(): boolean {
    return isEqual(this.incidentOriginal()!.actor, this.incidentEdit()!.actor);
  }

  // Marks if the rims structure was being changed during edit mode
  // Should switch to false when edit mode becomes false again
  public isRimsEdited = false;

  // Marks if the field references were being changed during edit mode
  public isFieldReferencesEdited = false;

  // Edit mode data

  // Article references
  public articleReferencesSnapshot = signal<ArticleReferenceModel[] | null>(null);
  public articleReferenceIdsToDelete = signal<number[] | null>(null);
  public articleReferencesToAdd = signal<ArticleReferenceModel[] | null>(null);
  public articleReferencesToMerge = signal<ArticleReferenceModel[] | null>(null);

  // If we are in edit mode, this computed returns a calculation of added/deleted references. Otherwise returns the references from the incident model.
  // The merging article references are intentionally not here because if we have them in the overall collection, they will appear in the merged incident in the merge view.
  public readonly allArticleReferences: Signal<ArticleReferenceModel[]> = computed(() => {
    if (this.isEditMode()) {
      const originals = this.articleReferencesSnapshot() ?? [];
      const added = this.articleReferencesToAdd() ?? [];
      const deleted = this.articleReferenceIdsToDelete() ?? [];

      return [...originals.filter((r) => !deleted.includes(r.id)), ...added];
    }

    return this.incident()?.articleReferences ?? [];
  });

  // Media references
  public mediaReferencesSnapshot = signal<MediaReferenceMetadataModel[] | null>(null);
  public mediaReferenceIdsToDelete = signal<number[] | null>(null);
  public mediaReferencesToAdd = signal<MediaReferenceMetadataCreateModel[] | null>(null);
  public mediaReferencesToMerge = signal<MediaReferenceMetadataModel[] | null>(null);

  // If we are in edit mode, this computed returns a calculation of added/deleted references. Otherwise returns the references from the incident model.
  // The merging media references are intentionally not here because if we have them in the overall collection, they will appear in the merged incident in the merge view.
  public readonly allMediaReferences: Signal<MediaReferenceMetadataModel[] | MediaReferenceMetadataCreateModel[]> =
    computed(() => {
      if (this.isEditMode()) {
        const originals = this.mediaReferencesSnapshot() ?? [];
        const added = this.mediaReferencesToAdd() ?? [];
        const deleted = this.mediaReferenceIdsToDelete() ?? [];

        return [...originals.filter((r) => !deleted.includes(r.id)), ...added];
      }

      return this.incident()?.mediaReferences ?? [];
    });

  // Detach/attach Incidents
  public duplicateIncidentIdsToDetach = signal<number[] | null>(null);
  public duplicateIncidentIdsToAttach = signal<number[] | null>(null);

  // -- Private members
  private resetData(): void {
    this.isRimsEdited = false;
    this.incidentEdit.set(null);

    this.articleReferencesSnapshot.set(null);
    this.articleReferenceIdsToDelete.set(null);
    this.articleReferencesToAdd.set(null);

    this.mediaReferencesSnapshot.set(null);
    this.mediaReferenceIdsToDelete.set(null);
    this.mediaReferencesToAdd.set(null);

    this.duplicateIncidentIdsToDetach.set(null);
    this.duplicateIncidentIdsToAttach.set(null);
  }

  private loadEditData(): void {
    // Article references defaults
    this.articleReferencesSnapshot.set(this.incident()?.articleReferences ?? []);
    this.articleReferenceIdsToDelete.set([]);
    this.articleReferencesToAdd.set([]);

    // Media references defaults
    this.mediaReferencesSnapshot.set(this.incident()?.mediaReferences ?? []);
    this.mediaReferenceIdsToDelete.set([]);
    this.mediaReferencesToAdd.set([]);
  }

  private resetMergeViewCache(): void {
    this.mergeViewOriginalIncident.set(null);
    this.mergeViewDuplicateIncidents.set(null);
  }
}
